import React, { useRef, useEffect } from 'react';
import { getCaretPosition, setCaretPosition } from '@/lib/cursor';
import type { User } from '@/api/users';
import { cn } from '@/lib/utils';

interface MentionInputProps {
    value: string;
    onChange: (value: string) => void;
    onTriggerMention: (query: string, position: { top: number; left: number }) => void;
    onCloseMention: () => void;
    userMap?: Record<string, User>;
    placeholder?: string;
    className?: string;
    autoFocus?: boolean;
    disabled?: boolean;
}

export function MentionInput({
    value,
    onChange,
    onTriggerMention,
    onCloseMention,
    userMap,
    placeholder,
    className,
    autoFocus,
    disabled
}: MentionInputProps) {
    const inputRef = useRef<HTMLDivElement>(null);
    const isComposing = useRef(false);
    const lastValue = useRef(value);

    // Helper to format the HTML content
    const formatContent = (text: string) => {
        // Replace @UUID with span
        const regex = /@([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})/g;

        // We need to handle newlines properly for contentEditable
        let html = text.replace(/\n/g, '<br>');

        html = html.replace(regex, (match, uuid) => {
            const user = userMap?.[uuid];
            const name = user
                ? (user.first_name && user.last_name ? `${user.first_name} ${user.last_name}` : user.email)
                : match; // Fallback if user not found

            // The data-preview attribute is used by CSS to show the name
            // The content is the ID, but hidden via CSS
            return `<span class="mention-highlight" data-preview="@${name}" contenteditable="false">${match}</span>`;
        });

        return html;
    };

    // Initial render and updates from parent
    useEffect(() => {
        if (inputRef.current && value !== inputRef.current.innerText) {
            // Only update if the text content is different to avoid cursor jumping on every keystroke
            // But wait, innerText of our formatted HTML is exactly the raw text (because spans are just wrappers)
            // So checking innerText is correct.

            // However, if we are typing, we don't want to re-render HTML on every char unless necessary
            // But we need to re-render to highlight new mentions.

            // For now, let's trust the parent value.
            if (value !== lastValue.current) {
                inputRef.current.innerHTML = formatContent(value);
                lastValue.current = value;
            }
        }
    }, [value, userMap]);

    useEffect(() => {
        if (autoFocus && inputRef.current) {
            inputRef.current.focus();
        }
    }, [autoFocus]);

    const handleInput = (e: React.FormEvent<HTMLDivElement>) => {
        if (isComposing.current) return;

        const element = e.currentTarget;
        const text = element.innerText;
        const caretPos = getCaretPosition(element);

        // Check for mention trigger
        // We look back from cursor
        const textBefore = text.slice(0, caretPos);
        const lastAt = textBefore.lastIndexOf('@');

        if (lastAt !== -1) {
            const query = textBefore.slice(lastAt + 1);
            // Simple check: no spaces allowed in query for now, or maybe allow spaces if we want "John Doe"
            // The reference implementation allows spaces until a newline or another @
            // Let's stick to simple word matching for now
            if (!/\s/.test(query)) {
                const rect = window.getSelection()?.getRangeAt(0).getBoundingClientRect();
                if (rect) {
                    onTriggerMention(query, { top: rect.bottom, left: rect.left });
                }
            } else {
                onCloseMention();
            }
        } else {
            onCloseMention();
        }

        // Update parent
        // We don't re-format here to avoid cursor issues. We let the parent update value, 
        // and then we might re-format in useEffect if needed.
        // Actually, if we don't re-format, the user sees plain text until save?
        // No, we want live highlighting.

        // To do live highlighting correctly without losing cursor:
        // 1. Save cursor pos
        // 2. Update HTML
        // 3. Restore cursor pos

        const newHtml = formatContent(text);
        if (element.innerHTML !== newHtml) {
            element.innerHTML = newHtml;
            setCaretPosition(element, caretPos);
        }

        lastValue.current = text;
        onChange(text);
    };

    return (
        <div
            ref={inputRef}
            contentEditable={!disabled}
            className={cn(
                "min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 overflow-auto whitespace-pre-wrap",
                className
            )}
            onInput={handleInput}
            onCompositionStart={() => (isComposing.current = true)}
            onCompositionEnd={() => {
                isComposing.current = false;
                if (inputRef.current) handleInput({ currentTarget: inputRef.current } as any);
            }}
            role="textbox"
            aria-multiline="true"
            data-placeholder={placeholder}
        />
    );
}
